import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    Stethoscope,
    Building2,
    Microscope,
    Truck,
    Globe2,
    HeartPulse,
    ClipboardCheck,
    Users,
    LineChart,
    Check,
} from "lucide-react";
import GlobeLines from "@/components/visual/GlobeLines";
import { FadeUp, Overline, SectionHeading } from "@/components/visual/SectionHeading";
import SEO from "@/components/seo/SEO";

const focusAreas = [
    { icon: Building2, title: "Healthcare Infrastructure", desc: "Hospitals, labs, and diagnostic networks — planning, structuring, and rollout." },
    { icon: Microscope, title: "Diagnostic & Lab Networks", desc: "Scalable diagnostic capacity for national and regional health systems." },
    { icon: HeartPulse, title: "Public Health Programs", desc: "Design and execution of large-scale, population-level interventions." },
    { icon: Truck, title: "Medical Supply Chain", desc: "Strategic advisory on procurement, logistics, and institutional supply." },
    { icon: Globe2, title: "International Collaborations", desc: "Cross-border health partnerships with institutional and multilateral bodies." },
];

const executionApproach = [
    { n: "01", title: "Needs Assessment", desc: "Evaluation of healthcare needs and infrastructure gaps." },
    { n: "02", title: "Strategic Planning", desc: "Program structuring, financial framing, and regulatory pathway design." },
    { n: "03", title: "Stakeholder Alignment", desc: "Coordination across government, private, and institutional partners." },
    { n: "04", title: "Execution Support", desc: "Operational rollout with on-ground program and vendor orchestration." },
    { n: "05", title: "Monitoring & Scale", desc: "Measurable outcomes, governance, and scalability planning." },
];

const services = [
    "Healthcare infrastructure planning (hospitals, labs, diagnostic networks)",
    "Public health program structuring and execution",
    "Medical supply chain advisory",
    "Government health initiatives and policy alignment",
    "Institutional partnerships and international collaboration",
];

const capabilities = [
    "Strategic planning for national and regional health systems",
    "Integration of private sector expertise with public programs",
    "Advisory on healthcare expansion in emerging markets",
    "Operational execution frameworks for health projects",
];

const Healthcare = () => {
    return (
        <div className="page-enter">
            <SEO
                title="Pharmaceuticals & Healthcare Advisory"
                description="Strategic advisory for healthcare systems, infrastructure, public health programs, medical supply chains, and government healthcare initiatives."
                path="/healthcare"
                keywords="healthcare advisory, public health advisory, healthcare infrastructure advisory, medical supply chain, pharmaceuticals advisory, hospital infrastructure"
            />
            {/* HERO */}
            <section className="relative min-h-[80vh] flex items-center overflow-hidden" data-testid="healthcare-hero">
                {/* Real project visual — modern diagnostic lab / healthcare facility */}
                <img
                    src="https://images.unsplash.com/photo-1579154204601-01588f351e67?auto=format&fit=crop&w=2400&q=80"
                    alt="Modern diagnostic laboratory"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-[#040B1A]/85" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#040B1A] via-[#040B1A]/80 to-[#040B1A]/40" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#040B1A]" />
                <GlobeLines className="opacity-[0.12]" />
                <div className="grain-overlay" />

                <div className="max-w-7xl mx-auto px-6 md:px-10 py-28 relative z-10 w-full">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="max-w-4xl"
                    >
                        <div className="inline-flex items-center gap-3 mb-10">
                            <span className="h-px w-10 bg-[#C9A14A]" />
                            <span className="overline">Pharmaceuticals & Healthcare Advisory</span>
                        </div>
                        <h1 className="font-serif text-5xl md:text-7xl lg:text-[5.25rem] text-white leading-[1.0] tracking-tight">
                            Pharmaceuticals &<br />
                            <span className="italic font-light text-[#C9A14A]">Healthcare Advisory.</span>
                        </h1>
                        <p className="mt-10 text-lg md:text-xl text-slate-300 font-light leading-[1.8] max-w-2xl">
                            Structured advisory for large-scale healthcare systems, infrastructure, and
                            government-led initiatives.
                        </p>
                        <div className="mt-12 flex flex-col sm:flex-row gap-5">
                            <Link to="/contact" className="btn-primary group" data-testid="healthcare-hero-cta">
                                Discuss Healthcare Initiative
                                <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                            </Link>
                            <Link to="/services" className="btn-secondary" data-testid="healthcare-hero-services">
                                Full Services
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* OVERVIEW */}
            <section className="py-24 md:py-32 border-t border-[#C9A14A]/15" data-testid="healthcare-overview">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
                    <FadeUp className="lg:col-span-4">
                        <Overline>Overview</Overline>
                        <h2 className="font-serif text-4xl md:text-5xl text-white mt-6 leading-[1.05]">
                            Bridging healthcare systems, infrastructure, and <em className="text-[#C9A14A] not-italic">strategic execution.</em>
                        </h2>
                    </FadeUp>
                    <FadeUp delay={0.15} className="lg:col-span-8 space-y-6 text-lg text-slate-300 font-light leading-[1.9]">
                        <p>
                            Indravax supports governments, institutions, and healthcare organisations in
                            structuring and executing large-scale public health and infrastructure projects.
                            The firm combines <span className="text-[#C9A14A]">medical expertise</span>,
                            strategic advisory, and operational execution to deliver sustainable
                            healthcare outcomes.
                        </p>
                        <p>
                            Our work spans national health programs, diagnostic network build-outs, medical
                            supply chain optimisation, and multilateral collaborations — aligned with
                            national and international development goals.
                        </p>
                        <p className="text-[#C9A14A] italic font-serif text-2xl pt-4">
                            "Integrating healthcare systems with strategic execution to deliver measurable impact at scale."
                        </p>
                    </FadeUp>
                </div>
            </section>

            {/* INFRASTRUCTURE VISUAL STRIP */}
            <section className="relative" data-testid="healthcare-visual-strip">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-y border-[#C9A14A]/15">
                    {[
                        { src: "https://images.unsplash.com/photo-1587351021355-a479a299d2f9?auto=format&fit=crop&w=1400&q=80", label: "Diagnostic Networks" },
                        { src: "https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&w=1400&q=80", label: "Hospital Infrastructure" },
                        { src: "https://images.unsplash.com/photo-1581093588401-fbb62a02f120?auto=format&fit=crop&w=1400&q=80", label: "Public Health Programs" },
                    ].map((img, i) => (
                        <div
                            key={img.label}
                            className={`relative aspect-[4/3] overflow-hidden ${i !== 2 ? "md:border-r border-[#C9A14A]/15" : ""}`}
                            data-testid={`healthcare-visual-${i}`}
                        >
                            <img
                                src={img.src}
                                alt={img.label}
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-out hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#040B1A] via-[#040B1A]/40 to-[#040B1A]/20" />
                            <div className="absolute bottom-6 left-6 right-6">
                                <div className="overline">0{i + 1}</div>
                                <div className="font-serif text-2xl md:text-3xl text-white mt-2 leading-tight">{img.label}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* KEY FOCUS AREAS */}
            <section className="py-24 md:py-32 bg-[#07132E]/40" data-testid="healthcare-focus">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <SectionHeading
                        overline="Key Focus Areas"
                        title="Five pillars, executed as one system."
                        description="Each focus area is delivered under an integrated engagement framework — from policy design to on-ground rollout."
                        testId="healthcare-focus-heading"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {focusAreas.map((f, i) => {
                            const Icon = f.icon;
                            return (
                                <FadeUp key={f.title} delay={i * 0.08}>
                                    <div className="institutional-card h-full" data-testid={`healthcare-focus-${i}`}>
                                        <div className="flex items-start justify-between">
                                            <Icon size={30} strokeWidth={1.2} className="text-[#C9A14A]" />
                                            <span className="text-[0.65rem] tracking-[0.3em] text-slate-500 uppercase">0{i + 1}</span>
                                        </div>
                                        <h3 className="font-serif text-2xl text-white mt-8">{f.title}</h3>
                                        <p className="text-slate-400 font-light mt-4 leading-relaxed">{f.desc}</p>
                                    </div>
                                </FadeUp>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* SERVICES + CAPABILITIES */}
            <section className="py-24 md:py-32" data-testid="healthcare-services">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12">
                    <FadeUp>
                        <div className="institutional-card h-full">
                            <Overline>Scope of Services</Overline>
                            <h3 className="font-serif text-3xl text-white mt-4 leading-tight">What we deliver</h3>
                            <ul className="mt-8 space-y-5">
                                {services.map((s) => (
                                    <li key={s} className="flex items-start gap-4 pb-5 border-b border-[#C9A14A]/10 last:border-0">
                                        <Check size={16} className="text-[#C9A14A] mt-1 shrink-0" strokeWidth={2} />
                                        <span className="text-slate-300 font-light">{s}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </FadeUp>
                    <FadeUp delay={0.1}>
                        <div className="institutional-card h-full">
                            <Overline>Capabilities</Overline>
                            <h3 className="font-serif text-3xl text-white mt-4 leading-tight">How we operate</h3>
                            <ul className="mt-8 space-y-5">
                                {capabilities.map((c) => (
                                    <li key={c} className="flex items-start gap-4 pb-5 border-b border-[#C9A14A]/10 last:border-0">
                                        <LineChart size={16} className="text-[#C9A14A] mt-1 shrink-0" strokeWidth={1.5} />
                                        <span className="text-slate-300 font-light">{c}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-8 inline-flex items-center gap-3">
                                <span className="h-px w-8 bg-[#C9A14A]" />
                                <span className="overline">Scalable · Sustainable · Structured</span>
                            </div>
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* EXECUTION APPROACH */}
            <section className="py-24 md:py-32 bg-[#07132E]/40 relative overflow-hidden" data-testid="healthcare-execution">
                <GlobeLines className="opacity-[0.07]" />
                <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
                    <SectionHeading
                        overline="Execution Approach"
                        title="A five-stage framework built for health systems."
                        description="From needs assessment to scaled rollout — every stage is engineered for measurable impact."
                        testId="healthcare-execution-heading"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-0 border-t border-[#C9A14A]/20">
                        {executionApproach.map((s, i) => (
                            <FadeUp
                                key={s.n}
                                delay={i * 0.08}
                                className={`p-8 md:p-10 ${i !== 4 ? "md:border-r border-[#C9A14A]/20" : ""} ${i !== 0 ? "border-t md:border-t-0 border-[#C9A14A]/20" : ""}`}
                            >
                                <div className="font-serif text-5xl text-[#C9A14A] italic">{s.n}</div>
                                <h3 className="font-serif text-2xl text-white mt-6">{s.title}</h3>
                                <p className="text-sm text-slate-400 mt-3 font-light leading-relaxed">{s.desc}</p>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* GOVERNMENT ENGAGEMENT + LEADERSHIP INTEGRATION */}
            <section className="py-24 md:py-32" data-testid="healthcare-government">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
                    <FadeUp>
                        <div className="institutional-card h-full">
                            <ClipboardCheck size={32} strokeWidth={1.1} className="text-[#C9A14A]" />
                            <Overline className="mt-6 block">Government Engagement</Overline>
                            <h3 className="font-serif text-3xl md:text-4xl text-white mt-4 leading-tight">
                                Aligned with national priorities.
                            </h3>
                            <p className="text-slate-300 font-light mt-6 leading-[1.9]">
                                Indravax works closely with public sector institutions to align healthcare
                                initiatives with national priorities, ensuring compliance, efficiency, and
                                long-term sustainability.
                            </p>
                        </div>
                    </FadeUp>
                    <FadeUp delay={0.1}>
                        <div className="institutional-card h-full">
                            <Users size={32} strokeWidth={1.1} className="text-[#C9A14A]" />
                            <Overline className="mt-6 block">Leadership Integration</Overline>
                            <h3 className="font-serif text-3xl md:text-4xl text-white mt-4 leading-tight">
                                Medical expertise at the partnership level.
                            </h3>
                            <p className="text-slate-300 font-light mt-6 leading-[1.9]">
                                This vertical is supported by medical and public health expertise within the
                                leadership team — enabling informed decision-making and effective program
                                execution at every stage of engagement.
                            </p>
                            <Link to="/about" className="mt-8 inline-flex items-center gap-2 text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] hover:gap-4 transition-all">
                                Meet Leadership <ArrowRight size={12} />
                            </Link>
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 md:py-28 border-t border-[#C9A14A]/15" data-testid="healthcare-cta">
                <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
                    <Overline>Healthcare Transformation</Overline>
                    <h2 className="font-serif text-4xl md:text-6xl text-white mt-8 leading-[1.05]">
                        Partner with Indravax for <br />
                        <span className="italic font-light text-[#C9A14A]">Healthcare Transformation.</span>
                    </h2>
                    <div className="mt-12 flex flex-col sm:flex-row justify-center gap-5">
                        <Link to="/contact" className="btn-primary" data-testid="healthcare-cta-discuss">
                            Discuss Healthcare Initiative <ArrowRight size={14} />
                        </Link>
                        <Link to="/sectors" className="btn-secondary" data-testid="healthcare-cta-sectors">
                            Explore Sectors
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Healthcare;
